import { buzz, tick } from './haptic'
import { el } from './ui'
import './diary-phone.css'

// The phone diary's Due bar (round 2 of docs/plans/diary-due/PLAN.md; Liam, 11 Oct 2026: "the to place should just be
// due and it should be on a sticky bar at the bottom so ... you could just swipe with your thumb"). It sits above the
// bottom tabs: the people due, as cards to swipe through sideways. Push a card UP onto a day (a date in the strip, a row
// of the week, or the day's list) and it is placed there; tap it and then tap a day; pull it DOWN and it leaves Due
// until next week ("seems good for dopamine aswell you could clear them all"). The handle opens the whole list.
// This file is only the bar, its gestures and the bottom sheet; what a card is and what placing does are the Diary's.

export interface BarCard {
  key: string
  name: string
  line: string     // "due 5 Sep", "was due 3 Oct, not done", "no day yet"
  late: boolean
  price: string
  pushed: number   // times moved to next week
  canPush: boolean // a customer due (a visit held in a week has a booking, so it is placed, never pushed)
  label: string
}

export interface BarOptions {
  cards: BarCard[]
  max: number
  picked: { key: string; name: string } | null // the card picked by a tap, waiting for a day
  later: number              // moved to next week
  newCount: number           // new enquiries
  waiting: number | null     // messaged and waiting on a reply, or offered days (null: not known yet)
  others: number             // in the list's other tabs (First visit, Gone quiet, No cycle set), only reached from here
  memory: string             // where this phone keeps whether the bar was left open, per round
  forceOpen?: boolean        // open whatever was left ("Show me" from What's next points at a card)
  dayWords: (iso: string) => string
  onPick: (key: string) => void
  onCancelPick: () => void
  onPlace: (key: string, iso: string) => void
  onPush: (key: string) => void
  onOpen: (tab: 'due' | 'new' | 'next') => void
  onWaiting: () => void
}

// Where the cards had been scrolled to, kept while the diary redraws under them.
let chipsLeft = 0
// A card being held (lifted or pulled). The diary waits for it before it redraws (a redraw would take the card from under
// the thumb and leave its floating copy on screen), then redraws once it is let go.
let live: (() => void) | null = null
let liveChip: HTMLElement | null = null
const afterLive: (() => void)[] = []
// A held card whose bar has gone (another screen) is let go here, so the diary can never wait on it for good.
export function dueDragLive(): boolean {
  if (live && liveChip && !liveChip.isConnected) live()
  return !!live
}
export function whenDueDragEnds(fn: () => void): void { afterLive.push(fn) }
function ended(): void {
  live = null
  liveChip = null
  for (const fn of afterLive.splice(0)) fn()
}
const PUSH_AT = 36 // pulled down this far, letting go moves them to next week
const START = 10   // a finger that has travelled this far has said which way it is going
// A card being dragged is aimed at the middle of its picture, which rides above the finger, not at the finger itself
// (Liam, 11 Oct 2026: the picture sat over Friday while his finger, on the Due bar, was over nothing, so letting go
// placed nobody).
const AIM = 45

// The tap a phone sends after a drag or swipe is let go is not a tap. The card or row under it may already be another
// one (the list and the bar redraw as soon as someone goes to next week), so it is swallowed wherever it lands.
// A touch screen sends no click after a drag, so the next real press ends the swallowing (review, round 2).
export function swallowNextTap(): void {
  const stop = (): void => { window.removeEventListener('click', swallow, true); window.removeEventListener('pointerdown', stop, true) }
  // Only a real tap: the buzz on letting go is a click Roundly makes itself on a hidden switch (haptic.ts, and the
  // switch passes it on as one the phone made), and swallowing it both lost the buzz and let the real tap through
  // (11 Oct 2026).
  const swallow = (e: Event): void => {
    if (!e.isTrusted || (e.target as Element | null)?.closest?.('.roundly-haptic')) return
    e.preventDefault(); e.stopImmediatePropagation(); stop()
  }
  window.addEventListener('click', swallow, true)
  window.addEventListener('pointerdown', stop, true)
  window.setTimeout(stop, 400)
}

// The phone Week's held names share the bar's redraw lock and end notifications.
export function trackDueDrag(card: HTMLElement, cancel: () => void): () => void {
  live?.()
  live = cancel; liveChip = card
  return () => { if (live === cancel) ended() }
}

const reducedMotion = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

// Folded or open (Liam, 19 Oct 2026, after user testing: the bar "is up all the time and user testing said it was
// annoying"; he picked a slim line, gone when there is nothing). Folded is one slim line, "Due · 4" and Waiting on; a tap
// or a swipe up opens the cards, and the bar stays as it was left on this phone. Nothing due, nothing waiting and
// nobody in the list: no bar at all.
const preferenceFallback = new Map<string, string | null>()
function readPreference(key: string): string | null {
  if (preferenceFallback.has(key)) return preferenceFallback.get(key) ?? null
  try { return localStorage.getItem(key) } catch { return null }
}
function savePreference(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value)
    preferenceFallback.delete(key)
  } catch { preferenceFallback.set(key, value) }
}
export function dueBarOpen(memory: string): boolean { return readPreference(memory) === '1' }
export function rememberDueBarOpen(memory: string, open: boolean): void { savePreference(memory, open ? '1' : null) }
export function setDueBarOpen(bar: HTMLElement, memory: string, open: boolean): void {
  rememberDueBarOpen(memory, open)
  bar.classList.toggle('is-folded', !open)
  const fold = bar.querySelector<HTMLElement>('.diary-due-fold')
  if (fold) { fold.textContent = open ? 'Hide' : 'Show'; fold.setAttribute('aria-expanded', String(open)) }
}

export function dueBar(o: BarOptions): HTMLElement {
  // Nothing left over from a card held when the bar was last drawn.
  live?.()
  for (const g of document.querySelectorAll('.diary-due-ghost')) g.remove()
  document.body.classList.remove('diary-due-dragging')
  const bar = el('section', { class: 'diary-due-bar', 'aria-label': 'Due' })
  // Nothing due, none moved to next week, no new enquiries and nobody in the list's other tabs: no bar while nobody is
  // waiting either. Waiting on's count can arrive or change after it is drawn; showWaiting shows or hides it then.
  if (!o.picked && !o.cards.length && !o.later && !o.newCount && !o.others) { bar.classList.add('is-empty'); bar.hidden = !o.waiting }
  // A card picked, or "Show me", needs the cards on screen.
  if (o.forceOpen) rememberDueBarOpen(o.memory, true)
  const open = !!o.picked || dueBarOpen(o.memory)
  if (!open) bar.classList.add('is-folded')
  const folded = (): boolean => bar.classList.contains('is-folded')
  const top = el('div', { class: 'diary-due-top' })
  const handle = el('button', { type: 'button', class: 'diary-due-handle', 'aria-label': 'Open the Due list' }, el('span', { 'aria-hidden': 'true' }))
  // Read out when it changes ("Tap a day for Ann"), as the computer's move bar is.
  const title = el('div', { class: 'diary-due-title', role: 'status' })
  if (o.picked) title.append(el('strong', {}, `Tap a day for ${o.picked.name}`))
  else {
    title.append(el('strong', { class: 'diary-due-count' }, o.cards.length ? `Due · ${o.cards.length}` : '✓ All clear'))
    if (o.cards.some(c => c.canPush)) title.append(el('span', { class: 'diary-due-hint' }, 'up onto a day · down for next week'))
    else if (o.cards.length) title.append(el('span', { class: 'diary-due-hint' }, 'up onto a day, or tap then a day'))
  }
  top.append(handle, title)
  if (o.picked) {
    const cancel = el('button', { type: 'button', class: 'diary-due-cancel' }, 'Cancel')
    cancel.addEventListener('click', () => o.onCancelPick())
    top.append(cancel)
  } else {
    const slot = el('span', { class: 'diary-due-waiting-slot' })
    const fold = el('button', { type: 'button', class: 'diary-due-fold', 'aria-expanded': String(open), 'aria-label': 'Show or hide the Due cards' }, open ? 'Hide' : 'Show')
    fold.addEventListener('click', () => setDueBarOpen(bar, o.memory, folded()))
    top.append(slot, fold)
    showWaiting(top, o.waiting, o.onWaiting)
  }
  // Folded, the handle and the title open the bar (a tap, or a swipe up); open, they open the list as before, and a
  // swipe down folds it again.
  for (const opener of [handle, title] as HTMLElement[]) {
    let from: { y: number; id: number } | null = null
    opener.addEventListener('pointerdown', (e) => { from = { y: e.clientY, id: e.pointerId } })
    opener.addEventListener('pointermove', (e) => {
      if (!from || e.pointerId !== from.id) return
      const up = from.y - e.clientY
      if (up >= 24) { from = null; if (folded()) setDueBarOpen(bar, o.memory, true); else o.onOpen('due') }
      else if (up <= -24 && !folded() && !o.picked) { from = null; setDueBarOpen(bar, o.memory, false) }
    })
    opener.addEventListener('pointercancel', () => { from = null })
    opener.addEventListener('click', () => { from = null; if (folded()) setDueBarOpen(bar, o.memory, true); else o.onOpen('due') })
  }
  bar.append(top)

  const chips = el('div', { class: 'diary-due-chips', role: 'group', 'aria-label': 'People due' })
  if (!o.cards.length) {
    const tickMark = el('span', { class: 'diary-due-tick', 'aria-hidden': 'true' }, '✓')
    const words = el('span', {}, el('strong', {}, 'All clear for this week.'), ` ${o.later ? `${o.later} moved to next week.` : 'Everyone is placed.'}`)
    if (o.later) {
      const see = el('button', { type: 'button', class: 'diary-due-see' }, 'See them')
      see.addEventListener('click', () => o.onOpen('next'))
      words.append(' ', see)
    }
    chips.append(el('div', { class: 'diary-due-clear' }, tickMark, words))
  }
  for (const c of o.cards.slice(0, o.max)) chips.append(chipFor(c, o, bar))
  if (o.cards.length > o.max) {
    const more = el('button', { type: 'button', class: 'diary-due-chip diary-due-more-card' },
      el('strong', {}, `+ ${o.cards.length - o.max} more`), el('span', { class: 'diary-due-line' }, 'see them all'))
    more.addEventListener('click', () => o.onOpen('due'))
    chips.append(more)
  }
  if (o.newCount) {
    const fresh = el('button', { type: 'button', class: 'diary-due-chip diary-due-new-card' },
      el('strong', {}, `New · ${o.newCount}`), el('span', { class: 'diary-due-line' }, o.newCount === 1 ? 'enquiry to place' : 'enquiries to place'))
    fresh.addEventListener('click', () => o.onOpen('new'))
    chips.append(fresh)
  }
  chips.addEventListener('scroll', () => { chipsLeft = chips.scrollLeft }, { passive: true })
  bar.append(chips)
  // Back where the thumb left it once drawn (a redraw starts the row at its first card otherwise).
  requestAnimationFrame(() => { if (chipsLeft && chips.isConnected) chips.scrollLeft = chipsLeft })
  return bar
}

// "Waiting on N" (Liam: "we often have to offer to several customers so small is good"): messaged and waiting on a
// reply, or offered days. Its count arrives after the bar is drawn (Needs you loads the round), so it is filled in then.
export function showWaiting(bar: ParentNode, count: number | null, open: () => void): void {
  const slot = bar.querySelector<HTMLElement>('.diary-due-waiting-slot')
  if (!slot) return
  // A bar with nothing else on it shows only while someone is waiting.
  const whole = slot.closest<HTMLElement>('.diary-due-bar')
  if (whole?.classList.contains('is-empty')) whole.hidden = !count
  if (!count) { slot.replaceChildren(); return }
  const chip = el('button', { type: 'button', class: 'diary-due-waiting', 'aria-label': `Waiting on ${count}: replies and offered days` }, `Waiting on ${count}`)
  chip.addEventListener('click', () => open())
  slot.replaceChildren(chip)
}

// Pop the count once a card has gone to next week: the small reward for clearing one.
export function popDueCount(root: ParentNode): void {
  if (reducedMotion()) return
  const count = root.querySelector<HTMLElement>('.diary-due-count')
  if (!count) return
  count.classList.remove('is-pop'); void count.offsetWidth; count.classList.add('is-pop')
}

// Where a lifted card would land: the date under the finger, if it takes people (a day that has gone does not).
function dropAt(x: number, y: number): HTMLElement | null {
  const under = document.elementFromPoint(x, y)
  return under?.closest<HTMLElement>('[data-due-drop]') ?? null
}

function chipFor(c: BarCard, o: BarOptions, bar: HTMLElement): HTMLElement {
  const picked = o.picked?.key === c.key
  const chip = el('button', { type: 'button', class: `diary-due-chip${picked ? ' is-picked' : ''}`, 'aria-pressed': String(picked), 'aria-label': c.label, 'data-due-key': c.key })
  const head = el('span', { class: 'diary-due-chip-head' }, el('strong', {}, c.name))
  if (c.price) head.append(el('span', { class: 'diary-due-price' }, c.price))
  const line = el('span', { class: `diary-due-line${c.late ? ' is-late' : ''}` }, c.line + (c.pushed ? ` · pushed ${c.pushed}×` : ''))
  chip.append(head, line)
  type Gesture = { id: number; x: number; y: number; mode: 'press' | 'lift' | 'push'; ghost?: HTMLElement; over?: HTMLElement | null; dy?: number; frame?: number; lastX?: number; lastY?: number; cleared?: boolean }
  let g: Gesture | null = null
  const targets = (on: boolean): void => { for (const d of document.querySelectorAll<HTMLElement>('[data-due-drop]')) d.classList.toggle('diary-due-target', on) }
  const scrollFrame = (): void => {
    if (!g || g.mode !== 'lift') return
    if (!chip.isConnected) { finish(); return } // the diary went away under it (another screen)
    // Brought back down to the bar, or up under the app's bar at the top, the page moves, so a day further down or up
    // can be reached (Week's last days sit under the bar: Liam, 11 Oct 2026). Down only once the card has been lifted
    // clear of the bar, so lifting it does not move the page; faster with the card over the bar itself.
    const barTop = bar.getBoundingClientRect().top
    const topEdge = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0
    const aim = (g.lastY ?? 0) - AIM
    if (aim < barTop - 80) g.cleared = true
    const was = window.scrollY
    if (g.cleared && aim > barTop - 50) window.scrollBy(0, aim > barTop ? 16 : 8)
    else if (aim < Math.max(0, topEdge) + 30) window.scrollBy(0, -8)
    // A still thumb sends no moves: the day lit and named follows the page as it moves (round 5 review).
    if (window.scrollY !== was && g.lastX !== undefined && g.lastY !== undefined) follow(g.lastX, g.lastY)
    g.frame = requestAnimationFrame(scrollFrame)
  }
  const follow = (x: number, y: number): void => {
    if (!g?.ghost) return
    g.lastX = x; g.lastY = y
    g.ghost.style.transform = `translate(${Math.round(x - 75)}px, ${Math.round(y - 70)}px) rotate(-3deg)`
    const over = dropAt(x, y - AIM)
    if (over !== g.over) {
      g.over?.classList.remove('diary-due-over')
      over?.classList.add('diary-due-over')
      g.over = over
      if (over) tick()
      const hint = g.ghost.querySelector('span')
      if (hint) hint.textContent = over?.dataset.dueDrop ? `on ${o.dayWords(over.dataset.dueDrop)}` : 'drop on a day'
    }
  }
  const restLine = (): void => { line.textContent = c.line + (c.pushed ? ` · pushed ${c.pushed}×` : '') }
  // While held, letting go anywhere ends it, even if the card itself loses the finger.
  const onWindowUp = (e: PointerEvent): void => { if (g && e.pointerId === g.id && g.mode !== 'press') up(e) }
  const onWindowCancel = (e: PointerEvent): void => { if (g && e.pointerId === g.id) { restLine(); finish() } }
  const hold = (): void => {
    live = finish
    liveChip = chip
    window.addEventListener('pointerup', onWindowUp, true)
    window.addEventListener('pointercancel', onWindowCancel, true)
  }
  const finish = (): void => {
    if (!g) return
    window.removeEventListener('pointerup', onWindowUp, true)
    window.removeEventListener('pointercancel', onWindowCancel, true)
    if (g.frame) cancelAnimationFrame(g.frame)
    g.ghost?.remove()
    g.over?.classList.remove('diary-due-over')
    targets(false)
    chip.classList.remove('is-lifted', 'is-pushing', 'is-push-ready')
    chip.style.transform = ''
    document.body.classList.remove('diary-due-dragging')
    try { if (chip.hasPointerCapture(g.id)) chip.releasePointerCapture(g.id) } catch { /* the pointer can already be gone */ }
    const was = g.mode
    g = null
    if (was !== 'press' && live === finish) ended()
  }
  chip.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (g) finish()
    g = { id: e.pointerId, x: e.clientX, y: e.clientY, mode: 'press' }
  })
  chip.addEventListener('pointermove', (e) => {
    if (!g || e.pointerId !== g.id) return
    const dx = e.clientX - g.x, dy = e.clientY - g.y
    if (g.mode === 'press') {
      // Sideways is the row scrolling: the phone takes it (the row only allows sideways panning).
      if (Math.abs(dx) > START && Math.abs(dx) > Math.abs(dy)) { g = null; return }
      if (dy < -START && Math.abs(dy) > Math.abs(dx)) {
        g.mode = 'lift'
        try { chip.setPointerCapture(e.pointerId) } catch { /* window listeners are not needed: capture is a nicety */ }
        const ghost = el('div', { class: 'diary-due-ghost', 'aria-hidden': 'true' }, c.name, el('span', {}, 'drop on a day'))
        document.body.append(ghost)
        g.ghost = ghost
        hold()
        chip.classList.add('is-lifted')
        document.body.classList.add('diary-due-dragging')
        targets(true)
        buzz('light')
        follow(e.clientX, e.clientY)
        g.frame = requestAnimationFrame(scrollFrame)
        return
      }
      if (dy > START && Math.abs(dy) > Math.abs(dx) && c.canPush) {
        g.mode = 'push'
        try { chip.setPointerCapture(e.pointerId) } catch { /* as above */ }
        chip.classList.add('is-pushing')
        hold()
      }
      return
    }
    if (g.mode === 'lift') { e.preventDefault(); follow(e.clientX, e.clientY); return }
    if (g.mode === 'push') {
      e.preventDefault()
      const down = Math.max(0, Math.min(70, dy))
      const ready = down > PUSH_AT
      if (ready !== (g.dy !== undefined && g.dy > PUSH_AT)) { if (ready) tick() }
      g.dy = down
      // The card follows the thumb a little way (the row it sits in would hide it further down); its colour and words say
      // when letting go will move them.
      chip.style.transform = `translateY(${Math.min(16, down)}px)`
      chip.classList.toggle('is-push-ready', ready)
      line.textContent = ready ? 'let go: next week' : c.line + (c.pushed ? ` · pushed ${c.pushed}×` : '')
    }
  })
  function up(e: PointerEvent): void {
    if (!g || e.pointerId !== g.id) return
    const done = g
    if (done.mode === 'press') { g = null; return } // a tap: the click that follows picks the card
    swallowNextTap()
    if (done.mode === 'lift') {
      const at = dropAt(e.clientX, e.clientY - AIM)?.dataset.dueDrop
      finish()
      if (at) { buzz('medium'); o.onPlace(c.key, at) }
      return
    }
    const pushed = (done.dy ?? 0) > PUSH_AT
    finish()
    restLine()
    if (pushed) { buzz('medium'); o.onPush(c.key) }
  }
  chip.addEventListener('pointerup', (e) => { if (g?.mode === 'press') up(e) })
  chip.addEventListener('pointercancel', (e) => { if (g && e.pointerId === g.id) { restLine(); finish() } })
  chip.addEventListener('lostpointercapture', (e) => { if (g && e.pointerId === g.id && !chip.isConnected) { restLine(); finish() } })
  chip.addEventListener('contextmenu', (e) => e.preventDefault())
  // Lifted or pulled down, the card keeps the finger: the phone may not take it to scroll the cards sideways, which ended
  // the drag and dropped the card (Liam, 11 Oct 2026). Pointer events cannot stop the phone; the touch itself can.
  chip.addEventListener('touchmove', (e) => { if (g && g.mode !== 'press') e.preventDefault() }, { passive: false })
  // A tap (or Enter on the card) picks it, to tap a day next; a drag that just ended is not a tap.
  chip.addEventListener('click', () => o.onPick(c.key))
  return chip
}

// A row of the Due list swiped left goes to next week, as a card pulled down does. Anything else stays a tap.
export function swipeToPush(row: HTMLElement, onPush: () => void): HTMLElement {
  const wrap = el('div', { class: 'diary-swipe' })
  const under = el('div', { class: 'diary-swipe-under', 'aria-hidden': 'true' }, 'Next week →')
  row.classList.add('diary-swipe-row')
  wrap.append(under, row)
  let g: { id: number; x: number; y: number; on: boolean; dx: number } | null = null
  row.addEventListener('pointerdown', (e) => {
    const t = e.target as Element
    if (['button', 'a', 'summary', 'input', 'select', '.row-menu'].some((s) => t.closest(s))) return
    g = { id: e.pointerId, x: e.clientX, y: e.clientY, on: false, dx: 0 }
  })
  row.addEventListener('pointermove', (e) => {
    if (!g || e.pointerId !== g.id) return
    const dx = e.clientX - g.x, dy = e.clientY - g.y
    if (!g.on) {
      if (Math.abs(dy) > START && Math.abs(dy) > Math.abs(dx)) { g = null; return }
      if (dx < -START && Math.abs(dx) > Math.abs(dy)) { g.on = true; try { row.setPointerCapture(e.pointerId) } catch { /* nicety */ } }
      else return
    }
    const was = g.dx
    g.dx = Math.max(-160, Math.min(0, dx))
    if ((was < -90) !== (g.dx < -90) && g.dx < -90) tick()
    row.style.transition = 'none'
    row.style.transform = `translateX(${g.dx}px)`
    under.textContent = g.dx < -90 ? 'Let go: next week' : 'Next week →'
    wrap.classList.toggle('is-ready', g.dx < -90)
  })
  const end = (push: boolean): void => {
    if (!g) return
    const was = g
    g = null
    row.style.transition = ''
    row.style.transform = ''
    wrap.classList.remove('is-ready')
    if (!was.on) return
    swallowNextTap()
    if (push && was.dx < -90) { buzz('medium'); onPush() }
  }
  row.addEventListener('pointerup', (e) => { if (g && e.pointerId === g.id) end(true) })
  row.addEventListener('pointercancel', () => end(false))
  return wrap
}

// The phone's bottom sheet: the Due list, Needs you, Waiting on. It closes from its handle, a tap above it, Escape, a
// pull down on the handle, or any change of screen (a name in it opens the customer).
export interface Sheet { dialog: HTMLDialogElement; head: HTMLElement; body: HTMLElement; close: () => void; resize?: (size: 'small' | 'large', remember?: boolean) => void }
export function bottomSheet(label: string, onClosed: () => void, week = false, memory?: { open: string; size: string }): Sheet {
  const dialog = el('dialog', { class: `diary-sheet${week ? ' diary-week-tray diary-new' : ''}`, 'aria-label': label }) as HTMLDialogElement
  const handle = el('button', { type: 'button', class: 'diary-sheet-handle', 'aria-label': `Close ${label}` }, el('span', { 'aria-hidden': 'true' }))
  const head = el('div', { class: 'diary-sheet-head' })
  const body = el('div', { class: 'diary-sheet-body' })
  // On a computer the sheet opens from the right with no handle: Close says how to put it away (round 3 review).
  // A ✕, like every other close in Roundly (Liam, 4 Nov 2026: "Close buttons should be an icon").
  const shut = el('button', { type: 'button', class: 'diary-sheet-close btn-secondary', 'aria-label': 'Close' }, el('span', { 'aria-hidden': 'true' }, '✕'))
  dialog.append(handle, shut, head, body)
  // Programmatic closes (navigation, picking or another sheet) preserve the owner's
  // preference. Only their explicit dismissal folds Due for the next drawing.
  const kept = week ? memory : undefined
  if (kept) { dialog.dataset.dueMemory = kept.open; rememberDueBarOpen(kept.open, true) }
  // A nonmodal small tray leaves Week scrollable. Large freezes the page at
  // its current position, including on iOS where overflow:hidden alone leaks.
  let unlockPage: (() => void) | null = null
  const lockPage = (lock: boolean): void => {
    if (!lock) { const unlock = unlockPage; unlockPage = null; unlock?.(); return }
    if (unlockPage) return
    const page = document.body, root = document.documentElement
    const x = window.scrollX ?? 0, y = window.scrollY ?? 0
    const before = {position:page.style.position, top:page.style.top, left:page.style.left, width:page.style.width, overflow:page.style.overflow}
    const rootOverflow = root.style.overflow
    page.style.position = 'fixed'; page.style.top = `${-y}px`; page.style.left = `${-x}px`
    page.style.width = '100%'; page.style.overflow = 'hidden'; root.style.overflow = 'hidden'
    unlockPage = () => {
      Object.assign(page.style, before); root.style.overflow = rootOverflow
      window.scrollTo?.(x, y)
    }
  }
  const close = (): void => { lockPage(false); if (dialog.open) dialog.close() }
  const dismiss = (): void => { if (kept) rememberDueBarOpen(kept.open, false); close() }
  let size: 'small' | 'large' = kept && readPreference(kept.size) === 'large' ? 'large' : 'small'
  const resize = (next: 'small' | 'large', remember = true): void => {
    if (kept && remember) savePreference(kept.size, next)
    size = next; dialog.dataset.size = size; dialog.style.height = ''
    lockPage(size === 'large')
    handle.setAttribute('aria-label', size === 'small' ? 'Make the tray bigger' : 'Make the tray smaller')
    handle.setAttribute('aria-expanded', String(size === 'large'))
    // Small leaves the actual strip in reach, including on a short phone or with larger text.
    const strip = document.querySelector<HTMLElement>('#screen.diary-phone-week-view .diary-week-strip')
    const available = window.innerHeight - (strip?.getBoundingClientRect().bottom ?? 0) - 12
    // Small opens up to just under the days (Liam, 3 Nov 2026: "should open more"), so names can be dragged onto them.
    dialog.style.setProperty('--week-tray-small', `${Math.max(96, Math.min(window.innerHeight * .62, available))}px`)
  }
  let dragged = false
  handle.addEventListener('click', () => { if (dragged) { dragged = false; return }; if (week) resize(size === 'small' ? 'large' : 'small'); else dismiss() })
  shut.addEventListener('click', dismiss)
  let pull: { y: number; id: number; height: number } | null = null
  handle.addEventListener('pointerdown', (e) => { dragged = false; pull = { y: e.clientY, id: e.pointerId, height: dialog.getBoundingClientRect().height }; if (week) { try { handle.setPointerCapture(e.pointerId) } catch { /* optional */ } } })
  handle.addEventListener('pointermove', (e) => {
    if (!pull || e.pointerId !== pull.id) return
    const dy = e.clientY - pull.y
    if (week) { if (Math.abs(dy) > 8) dragged = true; dialog.style.height = `${Math.max(96, Math.min(window.innerHeight * .86, pull.height - dy))}px` }
    else if (dy > 60) { pull = null; close() }
  })
  handle.addEventListener('pointerup', (e) => {
    if (!pull || e.pointerId !== pull.id) return
    const dy = e.clientY - pull.y; pull = null
    if (!week) return
    if (dragged) swallowNextTap()
    if (dy > 60 && size === 'small') dismiss()
    else resize(dy < -36 ? 'large' : dy > 36 ? 'small' : size)
  })
  handle.addEventListener('pointercancel', () => { pull = null; if (week) resize(size, false) })
  // A tap on the shaded page above the sheet lands on the dialog itself. Only one that started there: a row swiped away
  // is redrawn under the finger, and the click that follows the swipe also lands on the dialog.
  let downOnShade = false
  dialog.addEventListener('pointerdown', (e) => { downOnShade = e.target === dialog })
  dialog.addEventListener('click', (e) => { if (e.target === dialog && downOnShade) dismiss(); downOnShade = false })
  const leave = (): void => close()
  window.addEventListener('hashchange', leave)
  const fit = (): void => { if (week) resize(size, false) }
  let outsidePress: {id: number; x: number; y: number} | null = null
  const outside = (e: PointerEvent): void => {
    outsidePress = null
    if (!week || !dialog.open || !(e.target instanceof Node) || dialog.contains(e.target)) return
    // Leaving for another view/screen/list is not the owner's Hide choice.
    if ((e.target as Element).closest?.('a[href], .diary-views, .diary-ptile')) close()
    else outsidePress = {id:e.pointerId, x:e.clientX, y:e.clientY}
  }
  const outsideMove = (e: PointerEvent): void => {
    if (outsidePress && e.pointerId === outsidePress.id && Math.hypot(e.clientX - outsidePress.x, e.clientY - outsidePress.y) > 8) outsidePress = null
  }
  const outsideEnd = (e: PointerEvent): void => {
    const press = outsidePress; outsidePress = null
    if (press && press.id === e.pointerId && Math.hypot(e.clientX - press.x, e.clientY - press.y) <= 8) dismiss()
  }
  const outsideCancel = (): void => { outsidePress = null }
  // Do not let iOS pan the viewport from the exposed page/header of a large
  // tray. The list keeps native vertical panning and its own scroll boundary.
  const containTouch = (e: TouchEvent): void => {
    if (dialog.open && size === 'large' && !(e.target instanceof Node && body.contains(e.target))) e.preventDefault()
  }
  const escape = (e: KeyboardEvent): void => {
    if (!week || e.key !== 'Escape' || e.defaultPrevented || document.querySelector('dialog[open]:not(.diary-week-tray), details.row-menu[open]')) return
    e.preventDefault(); dismiss()
  }
  if (week) {
    window.addEventListener('resize', fit); document.addEventListener('keydown', escape, true)
    document.addEventListener('pointerdown', outside); document.addEventListener('pointermove', outsideMove)
    document.addEventListener('pointerup', outsideEnd); document.addEventListener('pointercancel', outsideCancel)
    document.addEventListener('touchmove', containTouch, {passive:false})
  }
  // The Undo bar and messages appear over the whole page, which an open sheet covers: while it is open they show
  // inside it, at its foot (a row moved to next week from the list can still be undone there), and go back after.
  const lift = (): void => { for (const n of document.querySelectorAll<HTMLElement>('body > #undo-bar, body > #toast')) dialog.append(n) }
  const watcher = typeof MutationObserver === 'function' ? new MutationObserver(lift) : null
  watcher?.observe(document.body, { childList: true })
  dialog.addEventListener('close', () => {
    lockPage(false)
    window.removeEventListener('hashchange', leave)
    window.removeEventListener('resize', fit); document.removeEventListener('keydown', escape, true)
    document.removeEventListener('pointerdown', outside)
    document.removeEventListener('pointermove', outsideMove); document.removeEventListener('pointerup', outsideEnd)
    document.removeEventListener('pointercancel', outsideCancel)
    document.removeEventListener('touchmove', containTouch)
    watcher?.disconnect()
    for (const n of dialog.querySelectorAll<HTMLElement>(':scope > #undo-bar, :scope > #toast')) document.body.append(n)
    dialog.remove()
    onClosed()
  })
  document.body.append(dialog)
  if (week) { dialog.setAttribute('aria-modal', 'false'); dialog.open = true; resize(size, false) } else dialog.showModal()
  // The sheet itself takes the focus as it opens, not its handle (whose ring then drew a dark line across the top);
  // Tab still reaches the handle, then the tabs and rows.
  dialog.setAttribute('tabindex', '-1')
  dialog.focus({ preventScroll: true })
  return { dialog, head, body, close, ...(week ? { resize } : {}) }
}
